// Story 011 — TanStack Query against GET /api/lyse/v1/data-acquisitions/:id/images.
// Returns the full ImageList for the active data acquisition. The Filmstrip
// (W9), Viewer (W2 — Story 012), and Tiles (W1 — Story 013) all read from
// this same query key; staleTime keeps refetches off the hot loop.
//
//
// SB6-ANA-CACHE-01 note: the returned `UseQueryResult` already exposes
// `refetch()`. CACHE-02 will call it to re-mint all SAS URLs in one request
// when `imageCache.fetchThrough` rejects with `ImageFetchError` status 403
// (expired SAS). No behavioural change is needed here for CACHE-01.
//
// SYNC: backend-requirement 015-get-inspection-images
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type { ImageList } from "@aeriosiq/types";

export const imageKeys = {
  all: ["images"] as const,
  list: (dataAcquisitionId: string) =>
    [...imageKeys.all, "list", dataAcquisitionId] as const,
};

export function useImagesQuery(
  dataAcquisitionId: string,
): UseQueryResult<ImageList> {
  const api = useApi();
  return useQuery<ImageList>({
    queryKey: imageKeys.list(dataAcquisitionId),
    enabled: dataAcquisitionId.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const response = await api.get<ImageList>(
        `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/images`,
        { params: { page: 1, pageSize: 500 } },
      );
      return response.data;
    },
  });
}
